// Client-side image resize + encrypt, the foundation of the encrypted-image
// pipeline. Photos/avatars are the one content path the relay could otherwise
// read; here we resize in the browser (the server can no longer do it once the
// bytes are ciphertext) and then encrypt the resized JPEG with the family's
// AES-256-GCM group key, exactly like every other content type.
//
// This module owns NO crypto of its own: it reuses the parity-locked
// encrypt/decrypt from crypto.ts (wire format nonce(12) || ciphertext ||
// tag(16)), so the wire format guardrail in crypto.test.ts stays authoritative.

import { encrypt } from "./crypto";

const DEFAULT_QUALITY = 0.85;

/**
 * Resize an image so its longer edge is at most `maxDim`, preserving aspect
 * ratio and never upscaling a smaller image, then re-encode as JPEG. The
 * canvas re-encode also strips EXIF metadata as a side effect (canvas does not
 * carry it over).
 */
export async function resizeImage(
  file: File | Blob,
  maxDim: number,
  quality?: number,
): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = bitmap;
    const longEdge = Math.max(width, height);
    const scale = longEdge > maxDim ? maxDim / longEdge : 1;
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = new OffscreenCanvas(targetW, targetH);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Failed to acquire 2D canvas context");
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    return await canvas.convertToBlob({
      type: "image/jpeg",
      quality: quality ?? DEFAULT_QUALITY,
    });
  } finally {
    bitmap.close();
  }
}

/**
 * Encrypt a (resized) image blob with the group key, returning the ciphertext
 * bytes in the standard wire format (nonce(12) || ciphertext || tag(16)). Reads
 * the blob's raw bytes and hands them to the generic crypto.ts `encrypt`.
 */
export async function encryptImageBlob(groupKey: Uint8Array, blob: Blob): Promise<Uint8Array> {
  const plaintext = new Uint8Array(await blob.arrayBuffer());
  return encrypt(groupKey, plaintext);
}
